import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faChevronDown,
  faChevronUp,
  faSortUp,
  faSortDown,
  faShip,
  faTruck,
  faPlane,
  faQuestion,
} from "@fortawesome/free-solid-svg-icons";

const TrafficGridControlTower = ({
  data,
  sortConfig,
  requestSort,
  expandedRows,
  handleRowClick,
  handleViewDetails,
  getOperationTypeText,
  getTransportIcon,
  currentPage,
  pageSize,
}) => {
  // Helper to format dates consistently
  const formatDate = (date) => {
    if (!date) return "N/A";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(date).toLocaleDateString(undefined, options);
  };

  // Helper to format weight with comma thousands separator
  const formatWeight = (weight) => {
    if (!weight) return "N/A";
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 4
    }).format(weight);
  };

  // Get status color class based on status
  const getStatusColorClass = (status) => {
    if (status === "Pagado") return "bg-green-100 text-green-800";
    return "bg-yellow-100 text-yellow-800";
  };

  return (
    <div className="bg-white shadow overflow-hidden rounded-lg">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <button
                  className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                  onClick={() => requestSort("reference")}
                >
                  <span>Reference</span>
                  {sortConfig.key === "reference" && (
                    <FontAwesomeIcon
                      icon={
                        sortConfig.direction === "asc" ? faSortUp : faSortDown
                      }
                      className="ml-1 text-blue-500"
                      size="xs"
                    />
                  )}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <button
                  className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                  onClick={() => requestSort("pedimento.pedimento_entry_point")}
                >
                  <span>Entry Point</span>
                  {sortConfig.key === "pedimento.pedimento_entry_point" && (
                    <FontAwesomeIcon
                      icon={
                        sortConfig.direction === "asc" ? faSortUp : faSortDown
                      }
                      className="ml-1 text-blue-500"
                      size="xs"
                    />
                  )}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <button
                  className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                  onClick={() => requestSort("pedimento.pedimento_number")}
                >
                  <span>Pedimento #</span>
                  {sortConfig.key === "pedimento.pedimento_number" && (
                    <FontAwesomeIcon
                      icon={
                        sortConfig.direction === "asc" ? faSortUp : faSortDown
                      }
                      className="ml-1 text-blue-500"
                      size="xs"
                    />
                  )}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <button
                  className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                  onClick={() => requestSort("trailer_number")}
                >
                  <span>Trailer #</span>
                  {sortConfig.key === "trailer_number" && (
                    <FontAwesomeIcon
                      icon={
                        sortConfig.direction === "asc" ? faSortUp : faSortDown
                      }
                      className="ml-1 text-blue-500"
                      size="xs"
                    />
                  )}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <button
                  className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                  onClick={() => requestSort("operation_type")}
                >
                  <span>Operation</span>
                  {sortConfig.key === "operation_type" && (
                    <FontAwesomeIcon
                      icon={
                        sortConfig.direction === "asc" ? faSortUp : faSortDown
                      }
                      className="ml-1 text-blue-500"
                      size="xs"
                    />
                  )}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <button
                  className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                  onClick={() => requestSort("customer_name")}
                >
                  <span>Customer</span>
                  {sortConfig.key === "customer_name" && (
                    <FontAwesomeIcon
                      icon={
                        sortConfig.direction === "asc" ? faSortUp : faSortDown
                      }
                      className="ml-1 text-blue-500"
                      size="xs"
                    />
                  )}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <button
                  className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                  onClick={() => requestSort("supplier")}
                >
                  <span>Supplier</span>
                  {sortConfig.key === "supplier" && (
                    <FontAwesomeIcon
                      icon={
                        sortConfig.direction === "asc" ? faSortUp : faSortDown
                      }
                      className="ml-1 text-blue-500"
                      size="xs"
                    />
                  )}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <button
                  className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                  onClick={() => requestSort("detail.dates_data.date_init")}
                >
                  <span>Created</span>
                  {sortConfig.key === "detail.dates_data.date_init" && (
                    <FontAwesomeIcon
                      icon={
                        sortConfig.direction === "asc" ? faSortUp : faSortDown
                      }
                      className="ml-1 text-blue-500"
                      size="xs"
                    />
                  )}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <button
                  className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                  onClick={() => requestSort("purchase_order")}
                >
                  <span>Purchase Order</span>
                  {sortConfig.key === "purchase_order" && (
                    <FontAwesomeIcon
                      icon={
                        sortConfig.direction === "asc" ? faSortUp : faSortDown
                      }
                      className="ml-1 text-blue-500"
                      size="xs"
                    />
                  )}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <button
                  className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                  onClick={() => requestSort("bill_of_lading_data.0.bill_of_lading")}
                >
                  <span>Bill of Lading</span>
                  {sortConfig.key === "bill_of_lading_data.0.bill_of_lading" && (
                    <FontAwesomeIcon
                      icon={
                        sortConfig.direction === "asc" ? faSortUp : faSortDown
                      }
                      className="ml-1 text-blue-500"
                      size="xs"
                    />
                  )}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <button
                  className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                  onClick={() => requestSort("total_packages")}
                >
                  <span>Total Packages</span>
                  {sortConfig.key === "total_packages" && (
                    <FontAwesomeIcon
                      icon={
                        sortConfig.direction === "asc" ? faSortUp : faSortDown
                      }
                      className="ml-1 text-blue-500"
                      size="xs"
                    />
                  )}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <button
                  className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                  onClick={() => requestSort("weight")}
                >
                  <span>Weight</span>
                  {sortConfig.key === "weight" && (
                    <FontAwesomeIcon
                      icon={
                        sortConfig.direction === "asc" ? faSortUp : faSortDown
                      }
                      className="ml-1 text-blue-500"
                      size="xs"
                    />
                  )}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <button
                  className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                  onClick={() => requestSort("detail.traffic_data.clave_ped")}
                >
                  <span>Clave Ped</span>
                  {sortConfig.key === "detail.traffic_data.clave_ped" && (
                    <FontAwesomeIcon
                      icon={
                        sortConfig.direction === "asc" ? faSortUp : faSortDown
                      }
                      className="ml-1 text-blue-500"
                      size="xs"
                    />
                  )}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <button
                  className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                  onClick={() => requestSort("detail.traffic_data.regime")}
                >
                  <span>Regime</span>
                  {sortConfig.key === "detail.traffic_data.regime" && (
                    <FontAwesomeIcon
                      icon={
                        sortConfig.direction === "asc" ? faSortUp : faSortDown
                      }
                      className="ml-1 text-blue-500"
                      size="xs"
                    />
                  )}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <button
                  className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                  onClick={() => requestSort("trafficStatus")}
                >
                  <span>Traffic Status</span>
                  {sortConfig.key === "trafficStatus" && (
                    <FontAwesomeIcon
                      icon={
                        sortConfig.direction === "asc" ? faSortUp : faSortDown
                      }
                      className="ml-1 text-blue-500"
                      size="xs"
                    />
                  )}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <button
                  className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                  onClick={() => requestSort("detail.traffic_data.status")}
                >
                  <span>Status</span>
                  {sortConfig.key === "detail.traffic_data.status" && (
                    <FontAwesomeIcon
                      icon={
                        sortConfig.direction === "asc" ? faSortUp : faSortDown
                      }
                      className="ml-1 text-blue-500"
                      size="xs"
                    />
                  )}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.length === 0 ? (
              <tr>
                <td colSpan="16" className="px-6 py-8 text-center text-gray-500">
                  No traffic data found matching your criteria
                </td>
              </tr>
            ) : (
              data.map((item, index) => {
                const isRowExpanded = expandedRows.includes(
                  item.header.reference
                );
                return (
                  <React.Fragment key={`${item.header.reference}-${index}`}>
                    <tr className="hover:bg-gray-50 cursor-pointer">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="font-medium">
                          {item.header.reference}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <FontAwesomeIcon
                            icon={
                              getTransportIcon(
                                item.header.pedimento.pedimento_entry_point
                              ).icon
                            }
                            className={`${
                              getTransportIcon(
                                item.header.pedimento.pedimento_entry_point
                              ).color
                            } mr-2`}
                            title={
                              getTransportIcon(
                                item.header.pedimento.pedimento_entry_point
                              ).title
                            }
                          />
                          <span>
                            {item.header.pedimento.pedimento_entry_point}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.header.pedimento.pedimento_number || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="font-medium">
                          {item.header.trailer_number}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getOperationTypeText(item.header.operation_type)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="font-medium">
                          {item.header.customer_name}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {item.header.customer_division}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.header?.detail?.invoice_data[0]?.supplier || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(item.header.detail?.dates_data?.date_init)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.header.purchase_order || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.header.bill_of_lading_data && item.header.bill_of_lading_data[0]
                          ? item.header.bill_of_lading_data[0].bill_of_lading || "N/A"
                          : "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.header.total_packages || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatWeight(item.header.weight)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.header.detail?.traffic_data?.clave_ped || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.header.detail?.traffic_data?.regime || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {item.header.trafficStatus || "N/A"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColorClass(
                            item.header.detail?.traffic_data?.status
                          )}`}
                        >
                          {item.header.detail?.traffic_data?.status === "Pagado"
                            ? "Paid"
                            : "Open"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(item);
                          }}
                          className="text-blue-600 hover:text-blue-800 mr-3"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(item.header.reference);
                          }}
                          className="text-gray-600 hover:text-gray-800"
                        >
                          <FontAwesomeIcon
                            icon={isRowExpanded ? faChevronUp : faChevronDown}
                          />
                        </button>
                      </td>
                    </tr>
                    {isRowExpanded && (
                      <tr>
                        <td colSpan="16" className="px-6 py-4 bg-gray-50">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {/* Reference Information */}
                            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                                <FontAwesomeIcon
                                  icon={faQuestion}
                                  className="mr-2 text-blue-500"
                                />
                                Reference Details
                              </h3>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-500">
                                    Reference:
                                  </span>
                                  <span className="font-medium">
                                    {item.header.reference}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">
                                    Trailer Number:
                                  </span>
                                  <span className="font-medium">
                                    {item.header.trailer_number || "N/A"}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Weight:</span>
                                  <span className="font-medium">
                                    {item.header.weight || "N/A"}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">
                                    Total Packages:
                                  </span>
                                  <span className="font-medium">
                                    {item.header.total_packages || "N/A"}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Customer Information */}
                            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                                <FontAwesomeIcon
                                  icon={faQuestion}
                                  className="mr-2 text-blue-500"
                                />
                                Customer Details
                              </h3>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-500">
                                    Customer:
                                  </span>
                                  <span className="font-medium">
                                    {item.header.customer_name}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">
                                    Division:
                                  </span>
                                  <span className="font-medium">
                                    {item.header.customer_division || "N/A"}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">RFC:</span>
                                  <span className="font-medium">
                                    {item.header.customer_rfc || "N/A"}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">
                                    Operation Type:
                                  </span>
                                  <span className="font-medium">
                                    {getOperationTypeText(
                                      item.header.operation_type
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Pedimento Information */}
                            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                                <FontAwesomeIcon
                                  icon={faQuestion}
                                  className="mr-2 text-blue-500"
                                />
                                Pedimento Details
                              </h3>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Number:</span>
                                  <span className="font-medium">
                                    {item.header.pedimento.pedimento_number ||
                                      "N/A"}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">
                                    Entry Point:
                                  </span>
                                  <span className="font-medium flex items-center">
                                    <FontAwesomeIcon
                                      icon={
                                        getTransportIcon(
                                          item.header.pedimento
                                            .pedimento_entry_point
                                        ).icon
                                      }
                                      className={`${
                                        getTransportIcon(
                                          item.header.pedimento
                                            .pedimento_entry_point
                                        ).color
                                      } mr-1`}
                                    />
                                    {
                                      item.header.pedimento
                                        .pedimento_entry_point
                                    }
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">
                                    License:
                                  </span>
                                  <span className="font-medium">
                                    {item.header.pedimento.pedimento_license ||
                                      "N/A"}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Status:</span>
                                  <span
                                    className={`font-medium ${getStatusColorClass(
                                      item.header.detail?.traffic_data?.status
                                    )}`}
                                  >
                                    {item.header.detail?.traffic_data
                                      ?.status === "Pagado"
                                      ? "Paid"
                                      : "Open"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="flex justify-end mt-4">
                            <button
                              onClick={() => handleViewDetails(item)}
                              className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-4 py-2 rounded"
                            >
                              View Full Details
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TrafficGridControlTower;
